import tw from 'twin.macro'
import React, { Fragment, useLayoutEffect, useRef, useState } from 'react'
import { CgChevronDown } from 'react-icons/cg'
import { Combobox } from '@headlessui/react'
import * as Portal from '@radix-ui/react-portal'

import { SuggestionContainer, SuggestionItem } from 'common/components/Suggestions/Suggestions'
import { inputBase } from 'common/components/Input/Input'
import { usePopper } from 'common/hooks/usePopper'
import { BUTTON_TYPES } from '../postButtonUtils'

// Dropdown choosing what the post's optional tap-through button opens.
// Adapted from the announcements TypeInput.
export default function ButtonTypeInput({
  inputRef: inputRefProp,
  value,
  setValue,
  clearErrors,
  buttonTypes = BUTTON_TYPES,
  label = 'Button (opens content when tapped)',
  placeholder = 'None',
  ariaLabel = 'Button',
  error,
}) {
  const comboboxBtnRef = useRef()
  // The options container is absolutely positioned and gets its width from this
  // measurement — without it the list renders zero-width (invisible sliver).
  // Measured in a layout effect: at first render the input isn't mounted yet,
  // and render-time measurement stays undefined until something re-renders.
  const localInputRef = useRef()
  const inputRef = inputRefProp || localInputRef
  const [inputWidth, setInputWidth] = useState(undefined)
  useLayoutEffect(() => {
    setInputWidth(inputRef.current?.getBoundingClientRect()?.width)
  }, [inputRef])

  const handleInputKeyDown = (e) => {
    const acceptedCodes = ['Enter', 'Tab', 'ArrowUp', 'ArrowDown']
    if (!acceptedCodes.includes(e.code)) {
      e.preventDefault()
    } else if (e.code === 'Enter' && !e.shiftKey) {
      comboboxBtnRef.current.click()
    }
  }

  let [popperTrigger, popperContainer] = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  return (
    <div className='mb-4'>
      <label className='inline-flex cursor-pointer font-semibold text-tBlack' tw='mb-1'>
        {label}
      </label>
      <Combobox
        value={value}
        onChange={(newValue) => {
          setValue('buttonType', newValue)
          if (value !== newValue) {
            clearErrors([
              'buttonType',
              'link',
              'scene',
              'videoId',
              'programId',
              'workoutId',
              'exerciseId',
              'collectionId',
              'programGroupId',
            ])
          }
        }}
      >
        <div ref={popperTrigger} className='w-full'>
          <Combobox.Button ref={comboboxBtnRef} as='div' className='relative w-full'>
            <Combobox.Input
              ref={(e) => {
                inputRef.current = e
              }}
              placeholder={placeholder}
              aria-label={ariaLabel}
              autoComplete='off'
              displayValue={(val) => buttonTypes.find(({ value }) => value === val)?.displayValue}
              onKeyDown={handleInputKeyDown}
              className='text-ellipsis bg-inherit cursor-pointer'
              css={[inputBase, tw`caret-transparent`, error && tw`ring-tRed focus:ring-tRed`]}
            />
            <CgChevronDown className='absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 w-4 h-4' />
          </Combobox.Button>
          {error && <p className='flex text-xs mt-1 text-tRed'>{error.message}</p>}
        </div>
        <Portal.Root>
          <Combobox.Options className='z-[999]' ref={popperContainer} modal={false}>
            <SuggestionContainer containerStyle={{ width: inputWidth }} scrollAreaClasses='!max-h-72'>
              {buttonTypes.map(({ value, displayValue }) => (
                <Combobox.Option as={Fragment} key={value} value={value}>
                  {({ active }) => (
                    <SuggestionItem active={active}>
                      <div className='flex items-center'>{displayValue}</div>
                    </SuggestionItem>
                  )}
                </Combobox.Option>
              ))}
            </SuggestionContainer>
          </Combobox.Options>
        </Portal.Root>
      </Combobox>
    </div>
  )
}
